/**
 * Duplicate/Orphan Detector (2026-10-06) — read-only review queue for Trips
 * and Travellers carrying the exact signature the itinerary-duplication bug
 * left behind before it was fixed: a record with zero real activity,
 * created within minutes of a sibling for the same person. Never deletes
 * anything automatically — Archive (existing soft-delete) is one click away
 * per row for a human to confirm.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Search, ShieldCheck, Archive } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fmtDate = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return s; }
};

export default function OrphanDetector() {
  const [data, setData] = useState({ trips: [], clients: [], totalTrips: 0, totalClients: 0 });
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    axios.get(`${BACKEND_URL}/api/admin/orphan-candidates`, { withCredentials: true })
      .then((r) => setData(r.data || { trips: [], clients: [] }))
      .catch((err) => toast.error(err?.response?.data?.detail || 'Failed to load orphan candidates'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const archiveTrip = async (tripId) => {
    if (!window.confirm('Archive this trip? It disappears from the list but every record stays intact — restore any time from Quotes → Show archived.')) return;
    try {
      await axios.post(`${BACKEND_URL}/api/trips/${tripId}/soft-delete`, {}, { withCredentials: true });
      toast.success('Trip archived');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to archive trip');
    }
  };

  const archiveClient = async (ucc) => {
    if (!window.confirm('Archive this traveller? They disappear from the list but every record stays intact — restore any time from Travellers → Show archived.')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/clients/${ucc}`, { withCredentials: true });
      toast.success('Traveller archived');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to archive traveller');
    }
  };

  const tripRows = data.trips || [];
  const clientRows = data.clients || [];

  return (
    <div className="space-y-6 p-6 max-w-6xl mx-auto" data-testid="orphan-detector-page">
      <header>
        <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
          <Search className="h-6 w-6 text-amber-600" /> Duplicate / Orphan Detector
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">
          Read-only. Flags trips or travellers with zero real activity created within minutes of a sibling
          record for the same person — the exact signature the itinerary-duplication bug left behind.
          Nothing here is ever auto-deleted; use Archive to soft-delete after you've confirmed it's a duplicate.
        </p>
      </header>

      {loading ? (
        <div className="text-center py-12 text-slate-400 text-sm">Loading…</div>
      ) : (
        <>
          <Card className="p-0 overflow-hidden" data-testid="orphan-trips-card">
            <div className="px-4 py-3 bg-slate-50 border-b flex items-center justify-between">
              <h2 className="text-sm font-semibold text-slate-800">Orphan trip candidates</h2>
              <span className="text-xs font-mono text-slate-500" data-testid="orphan-trips-count">{data.totalTrips || 0}</span>
            </div>
            {tripRows.length === 0 ? (
              <div className="p-10 text-center" data-testid="orphan-trips-empty">
                <ShieldCheck className="h-8 w-8 text-emerald-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500">No orphan trip candidates found.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="orphan-trips-table">
                  <thead className="bg-slate-50/60 border-b">
                    <tr>
                      <th className="px-4 py-2 text-left text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Orphan candidate</th>
                      <th className="px-4 py-2 text-left text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Likely original</th>
                      <th className="px-4 py-2 text-left text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Guest</th>
                      <th className="px-4 py-2 text-right text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Created Δ</th>
                      <th className="px-4 py-2 text-right text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {tripRows.map((r) => (
                      <tr key={r.orphanTripId} data-testid={`orphan-trip-row-${r.orphanTripId}`}>
                        <td className="px-4 py-2">
                          <Link to={`/quotes/${encodeURIComponent(r.orphanTripId)}/edit`} className="text-teal-700 hover:underline font-mono text-xs">{r.orphanDisplayId}</Link>
                          <div className="text-[10px] text-slate-400">{r.orphanDestination || '—'} · {fmtDate(r.orphanCreatedAt)}</div>
                        </td>
                        <td className="px-4 py-2">
                          <Link to={`/quotes/${encodeURIComponent(r.originalTripId)}/edit`} className="text-slate-600 hover:underline font-mono text-xs">{r.originalDisplayId}</Link>
                          <div className="text-[10px] text-slate-400">{fmtDate(r.originalCreatedAt)}</div>
                        </td>
                        <td className="px-4 py-2 text-xs text-slate-600">{r.guestName || '—'}</td>
                        <td className="px-4 py-2 text-right font-mono text-xs text-amber-700">{r.deltaSeconds}s</td>
                        <td className="px-4 py-2 text-right">
                          <Button
                            data-testid={`archive-orphan-trip-btn-${r.orphanTripId}`}
                            variant="ghost" size="sm"
                            onClick={() => archiveTrip(r.orphanTripId)}
                            className="text-amber-600 hover:text-amber-800"
                          >
                            <Archive className="h-4 w-4 mr-1.5" /> Archive
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <Card className="p-0 overflow-hidden" data-testid="orphan-clients-card">
            <div className="px-4 py-3 bg-slate-50 border-b flex items-center justify-between">
              <h2 className="text-sm font-semibold text-slate-800">Orphan traveller candidates</h2>
              <span className="text-xs font-mono text-slate-500" data-testid="orphan-clients-count">{data.totalClients || 0}</span>
            </div>
            {clientRows.length === 0 ? (
              <div className="p-10 text-center" data-testid="orphan-clients-empty">
                <ShieldCheck className="h-8 w-8 text-emerald-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500">No orphan traveller candidates found.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="orphan-clients-table">
                  <thead className="bg-slate-50/60 border-b">
                    <tr>
                      <th className="px-4 py-2 text-left text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Orphan candidate</th>
                      <th className="px-4 py-2 text-left text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Likely original</th>
                      <th className="px-4 py-2 text-left text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Matched on</th>
                      <th className="px-4 py-2 text-right text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Created Δ</th>
                      <th className="px-4 py-2 text-right text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {clientRows.map((r) => (
                      <tr key={r.orphanUcc} data-testid={`orphan-client-row-${r.orphanUcc}`}>
                        <td className="px-4 py-2">
                          <Link to={`/clients/${r.orphanUcc}`} className="text-teal-700 hover:underline text-xs font-medium">{r.orphanName || r.orphanUcc}</Link>
                          <div className="text-[10px] text-slate-400 font-mono">{r.orphanUcc} · {fmtDate(r.orphanCreatedAt)}</div>
                        </td>
                        <td className="px-4 py-2">
                          <Link to={`/clients/${r.originalUcc}`} className="text-slate-600 hover:underline text-xs">{r.originalName || r.originalUcc}</Link>
                          <div className="text-[10px] text-slate-400 font-mono">{r.originalUcc} · {fmtDate(r.originalCreatedAt)}</div>
                        </td>
                        <td className="px-4 py-2 text-xs text-slate-500 capitalize">{r.matchedOn}</td>
                        <td className="px-4 py-2 text-right font-mono text-xs text-amber-700">{r.deltaSeconds}s</td>
                        <td className="px-4 py-2 text-right">
                          <Button
                            data-testid={`archive-orphan-client-btn-${r.orphanUcc}`}
                            variant="ghost" size="sm"
                            onClick={() => archiveClient(r.orphanUcc)}
                            className="text-amber-600 hover:text-amber-800"
                          >
                            <Archive className="h-4 w-4 mr-1.5" /> Archive
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
